import React, { useState } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faSave, faXmark, faCheck, faFileLines } from '@fortawesome/free-solid-svg-icons';
import Modal from "@/components/Modal"; // make sure Modal is imported

const EditableTable = ({
    headers,
    rows,
    editRowId,
    editedData,
    handleEdit,
    handleSave,
    handleCancel,
    handleChange,
    editableFields,
    handleLog,
    hideChangeLog
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentLog, setCurrentLog] = useState(null);

    const showActionColumn = editableFields?.length > 0;

    const openModal = (data) => {
        setCurrentLog(data);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setCurrentLog(null);
    };

    // Render nested objects and arrays in a readable format
    const renderValue = (value) => {
        if (Array.isArray(value)) {
            return value.join(", ");
        } else if (typeof value === "object" && value !== null) {
            // Check if it's an object with numeric keys like "code_product"
            if (Object.keys(value).every(key => !isNaN(key))) {
                return (
                    <div className="pl-4">
                        {Object.entries(value).map(([key, subValue]) => (
                            <div key={key} className="space-y-2">
                                <strong>Product {key}:</strong>
                                <div className="ml-4">
                                    {Object.entries(subValue).map(([subKey, subVal]) => (
                                        <div key={subKey}>
                                            <strong>{subKey}:</strong> {String(subVal)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                );
            } else {
                return (
                    <div className="pl-4">
                        {Object.entries(value).map(([subKey, subValue]) => (
                            <div key={subKey}>
                                <strong>{subKey}:</strong> {String(subValue)}
                            </div>
                        ))}
                    </div>
                );
            }
        } else {
            return String(value);
        }
    };

    // ChangeLog component to display values in text format
    const ChangeLog = ({ data }) => {
        if (!data) return null;
        return (
            <div className="space-y-2 text-sm">
                {Object.entries(data).map(([key, value]) => (
                    <div key={key} className="border-b pb-1">
                        <strong>{key}:</strong> {renderValue(value)}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="tableContainer">
            <table className="table text-xs w-[88vw]" role="table">
                <thead className="stickyHeader bg-[var(--mainCol)] text-white">
                    <tr>
                        {headers?.map((header, index) => (
                            <th className="p-1 text-left border" key={index}>
                                {header.toUpperCase()}
                            </th>
                        ))}
                        {showActionColumn && (
                            <th style={{ background: "var(--mainCol)" }} className="p-1 text-left border stickyActionColumn z-100">
                                ACTION
                            </th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {rows?.map((row) => (
                        <tr key={row.id}>
                            {headers.map((header) => (
                                <td key={header} className="p-3 border">
                                    {editRowId === row.id && editableFields?.includes(header) ? (
                                        header === 'active' || header === 'status' || header === 'is_read' ||
                                        header === 'is_Finshed' || header === 'is_Start' || header === 'queryResolve' ? (
                                            <div className="cursor-pointer flex justify-center">
                                                {editedData[header] === true && (
                                                    <FontAwesomeIcon onClick={(e) => handleChange(e, header, true)} color="var(--green_Main)" icon={faCheck} />
                                                )}
                                                {editedData[header] === false && (
                                                    <FontAwesomeIcon onClick={(e) => handleChange(e, header, false)} color="indianred" icon={faXmark} />
                                                )}
                                            </div>
                                        ) : (
                                            <input
                                                type="text"
                                                value={editedData[header] || ''}
                                                onChange={(e) => handleChange(e, header)}
                                                onKeyUp={(e) => e.key === 'Enter' && handleSave()}
                                                className="w-full p-1 border"
                                            />
                                        )
                                    ) : (
                                        typeof row[header] === 'object' && row[header] !== null ? (
                                            <button
                                                className="text-blue-600 underline text-xs outline-none"
                                                onClick={() => openModal(row[header])}
                                            >
                                                Show Values
                                            </button>
                                        ) : row[header] === true ? (
                                            <FontAwesomeIcon className="ml-2" color="var(--green_Main)" icon={faCheck} />
                                        ) : row[header] === false ? (
                                            <FontAwesomeIcon className="ml-2" color="indianred" icon={faXmark} />
                                        ) : (
                                            String(row[header])
                                        )
                                    )}
                                </td>
                            ))}
                            {showActionColumn && (
                                <td className="p-2 border stickyActionColumn">
                                    {editRowId === row.id ? (
                                        <div className="flex justify-center">
                                            <FontAwesomeIcon
                                                className="p-2 cursor-pointer"
                                                color="#67a97b"
                                                onClick={handleSave}
                                                icon={faSave}
                                            />
                                            <FontAwesomeIcon
                                                className="p-2 cursor-pointer"
                                                color="indianred"
                                                onClick={handleCancel}
                                                icon={faXmark}
                                            />
                                        </div>
                                    ) : (
                                        <div className="flex justify-center z-100">
                                            <FontAwesomeIcon
                                                className="cursor-pointer"
                                                onClick={() => handleEdit(row.id)}
                                                icon={faPenToSquare}
                                            />
                                            {!hideChangeLog && (
                                                <FontAwesomeIcon className="ml-2 cursor-pointer" onClick={() => handleLog(row.id)} icon={faFileLines} />
                                            )}
                                        </div>
                                    )}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Modal to Show Values */}
            <Modal
                isOpen={isModalOpen}
                onClose={closeModal}
                title="Show Values"
                content={<ChangeLog data={currentLog} />}
            />
        </div>
    );
};

export default EditableTable;
